import React, { useState, useEffect } from 'react';
import api from './api';
import { formatTimeAgo } from './utils';

const MyPosts = ({ onBack }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tab, setTab] = useState('posts'); // 'posts' or 'followed'
  const [followedThreads, setFollowedThreads] = useState([]);

  useEffect(() => {
    if (tab === 'posts') {
      loadPosts();
    } else {
      loadFollowed();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/posts/mine');
      setItems(res.data.items);
      setHasMore(res.data.hasMore);
      setNextCursor(res.data.nextCursor);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  const loadFollowed = async () => {
    setLoading(true);
    try {
      const res = await api.get('/threads/followed');
      setFollowedThreads(res.data);
    } catch {
      setFollowedThreads([]);
    }
    setLoading(false);
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get(`/posts/mine?before=${encodeURIComponent(nextCursor)}`);
      setItems(prev => [...prev, ...res.data.items]);
      setHasMore(res.data.hasMore);
      setNextCursor(res.data.nextCursor);
    } catch {
      // ignore
    }
    setLoadingMore(false);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'thread': return '\uD83D\uDCDD';
      case 'comment': return '\uD83D\uDCAC';
      case 'review': return '\u2B50';
      case 'story': return '\uD83D\uDCD6';
      default: return '\u270F\uFE0F';
    }
  };

  const getLabel = (type) => {
    switch (type) {
      case 'thread': return 'Thread';
      case 'comment': return 'Comment';
      case 'review': return 'Review';
      case 'story': return 'Story';
      default: return 'Post';
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)',
        borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#FF8A3D', fontSize: '1.5rem', cursor: 'pointer', padding: '0.25rem' }}>
          {'\u2190'}
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>My Posts</h1>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '1.5rem' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {['posts', 'followed'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '0.5rem 1.25rem',
                background: tab === t ? '#FF8A3D' : 'transparent',
                border: tab === t ? 'none' : '1px solid rgba(255, 138, 61, 0.3)',
                color: tab === t ? '#0B0B10' : '#FF8A3D',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              {t === 'posts' ? 'My Posts' : 'Following'}
            </button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '3rem' }}>Loading...</p>
        ) : tab === 'posts' ? (
          items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#8b9cb3' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{'\u270F\uFE0F'}</div>
              <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>No posts yet</h3>
              <p>Your threads, comments, reviews, and stories will appear here.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {items.map((item, i) => (
                  <div key={`${item.post_type}-${item.id}-${i}`} style={{
                    background: '#16161D',
                    border: '1px solid rgba(255, 138, 61, 0.1)',
                    borderRadius: '12px',
                    padding: '1rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.95rem' }}>{getIcon(item.post_type)}</span>
                      <span style={{ color: '#FF8A3D', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>
                        {getLabel(item.post_type)}
                      </span>
                      {(item.community_name || item.race_name) && (
                        <span style={{ color: '#8b9cb3', fontSize: '0.75rem' }}>
                          in {item.community_name || item.race_name}
                        </span>
                      )}
                      <span style={{ color: '#5a6a7c', fontSize: '0.75rem', marginLeft: 'auto' }}>
                        {formatTimeAgo(item.created_at)}
                      </span>
                    </div>
                    {item.title && (
                      <div style={{ color: '#FFFFFF', fontWeight: 600, marginBottom: '0.25rem' }}>
                        {item.title}
                      </div>
                    )}
                    {item.content && (
                      <div style={{ color: '#8b9cb3', fontSize: '0.9rem', lineHeight: 1.5 }}>
                        {item.content}{item.content.length >= 200 ? '...' : ''}
                      </div>
                    )}
                    {item.overall_rating && (
                      <div style={{ marginTop: '0.25rem' }}>
                        {Array.from({ length: 5 }, (_, i) => (
                          <span key={i} style={{ color: i < item.overall_rating ? '#FF8A3D' : '#2A2A35', fontSize: '0.85rem' }}>
                            {'\u2605'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {hasMore && (
                <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    style={{
                      padding: '0.75rem 2rem',
                      background: 'transparent',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      color: '#FF8A3D',
                      borderRadius: '25px',
                      cursor: loadingMore ? 'default' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      opacity: loadingMore ? 0.6 : 1,
                    }}
                  >
                    {loadingMore ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )
        ) : (
          followedThreads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#8b9cb3' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{'\uD83D\uDD14'}</div>
              <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>No followed threads</h3>
              <p>Threads you create or comment on are automatically followed.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {followedThreads.map(thread => (
                <div key={thread.id} style={{
                  background: '#16161D',
                  border: '1px solid rgba(255, 138, 61, 0.1)',
                  borderRadius: '12px',
                  padding: '1rem',
                }}>
                  <div style={{ color: '#FFFFFF', fontWeight: 600, marginBottom: '0.35rem' }}>
                    {thread.title}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#8b9cb3', fontSize: '0.8rem' }}>
                    <span>{thread.community_name}</span>
                    <span>{'\uD83D\uDCAC'} {thread.comment_count}</span>
                    <span style={{ marginLeft: 'auto', color: '#5a6a7c' }}>{formatTimeAgo(thread.updated_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MyPosts;
